import { useCallback, useMemo } from 'react';
import type { ServiceGateway, ServiceOverview } from '@forq/service-data';
import type { ResolvedPeriod } from './period';
import { ReportActions, type SectionReport } from './report';
import { CustomizableMetrics } from '@forq/ui';
import { Kpi, KpiGrid, num, SectionShell, useAsyncData } from './shared';

/** Service-Auswertung. Momentaufnahme: Auslastung der Werkstatt im aktuellen Auftragsfluss. */
export function ServiceSection({ tenantId, gateway, period, tenantName }: {
  readonly tenantId: string;
  readonly gateway: ServiceGateway;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  const load = useCallback((): Promise<ServiceOverview> => gateway.getOverview(tenantId), [gateway, tenantId]);
  const { data, loading, error, reload } = useAsyncData(load);

  const report: SectionReport | null = useMemo(() => {
    if (!data) return null;
    return {
      title: 'Service',
      kpis: [
        { label: 'In Planung', value: num(data.planningCount) },
        { label: 'Ausgegeben', value: num(data.issuedCount) },
        { label: 'Beleg zurück', value: num(data.paperReturnedCount) },
        { label: 'Freigabe offen', value: num(data.approvalPendingCount) },
        { label: 'Abrechnungsreif', value: num(data.billingReadyCount) },
      ],
      blocks: [],
    };
  }, [data]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {data && report ? (
        <div className="auswertung__stack">
          <div className="auswertung__sectionhead">
            <span className="sales-eyebrow">Service · Momentaufnahme</span>
            <ReportActions report={report} period={period} tenantName={tenantName} />
          </div>
          <KpiGrid>
            <CustomizableMetrics
              pageKey="auswertung-service"
              tiles={[
                { key: 'planning', node: <Kpi label="In Planung" value={num(data.planningCount)} hint="noch einzuplanen" /> },
                { key: 'issued', node: <Kpi label="Ausgegeben" value={num(data.issuedCount)} hint="Auftrag in der Werkstatt" /> },
                { key: 'paperreturn', node: <Kpi label="Beleg zurück" value={num(data.paperReturnedCount)} hint="Rückmeldung erfasst" /> },
                { key: 'approval', node: <Kpi label="Freigabe offen" value={num(data.approvalPendingCount)} hint="wartet auf Freigabe" /> },
                { key: 'billingready', node: <Kpi label="Abrechnungsreif" value={num(data.billingReadyCount)} hint="an Finance übergeben" /> },
              ]}
            />
          </KpiGrid>
        </div>
      ) : null}
    </SectionShell>
  );
}
